import { KeyRound, ShieldCheck, Vault } from "lucide-react";

import { useVaultMode } from "@/hooks/use-vault-mode";
import { VaultOnboardingContainer } from "./vault-onboarding-container";
import { TrusteeDashboardContainer } from "@/features/trustee/containers/trustee-dashboard-container";

/**
 * Vault home. Resolves which surface to show from the user's vault capabilities
 * (`vault_is_owner` / `vault_is_heir`):
 *  - owner-only  → the owner console (files, trustees, unlock);
 *  - heir-only   → the trustee dashboard (shards + unlock portal);
 *  - both        → a one-time picker (remembered), switchable from the header;
 *  - neither     → an empty state.
 *
 * This is what structurally keeps owners out of heir views and heirs out of
 * owner views: a heir never renders the owner console (files, trustees, and the
 * on-demand vault-password prompt), and vice versa. The owner console no longer
 * blocks on a password at landing — it is requested only when the master key is
 * needed (adding/opening a document).
 */
export function VaultHomeContainer() {
  const { effectiveMode, isOwner, isHeir, setMode } = useVaultMode();

  if (effectiveMode === "owner") return <VaultOnboardingContainer />;
  if (effectiveMode === "heir") return <TrusteeDashboardContainer />;

  // No vault relationships on the token.
  if (!isOwner && !isHeir) {
    return (
      <div className="mx-auto max-w-md space-y-3 py-24 text-center">
        <div className="bg-muted text-muted-foreground mx-auto flex h-12 w-12 items-center justify-center rounded-full">
          <Vault className="h-6 w-6" />
        </div>
        <h1 className="text-lg font-semibold">No vaults yet</h1>
        <p className="text-muted-foreground text-sm">
          This account isn't linked to a vault yet. If your adviser has set one
          up for you, or someone has added you as a trustee, try signing in
          again after they let you know it is ready.
        </p>
      </div>
    );
  }

  // Dual-role user who hasn't chosen yet.
  return (
    <div className="mx-auto max-w-3xl space-y-8 py-12">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-bold tracking-tight">
          How would you like to continue?
        </h1>
        <p className="text-muted-foreground text-sm">
          You have your own vault and are also a trustee for someone else.
          Choose what you would like to do. You can switch at any time.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <button
          onClick={() => setMode("owner")}
          className="bg-card border-border hover:border-primary/40 flex flex-col items-start gap-4 rounded-2xl border p-6 text-left shadow-sm transition-all"
        >
          <div className="bg-primary/10 text-primary flex h-12 w-12 items-center justify-center rounded-full">
            <KeyRound className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            <h2 className="text-base font-semibold">My Vault</h2>
            <p className="text-muted-foreground text-sm">
              View your documents and manage your recovery arrangements.
            </p>
          </div>
          <span className="text-primary mt-2 text-xs font-semibold">
            Open my vault →
          </span>
        </button>

        <button
          onClick={() => setMode("heir")}
          className="bg-card border-border hover:border-primary/40 flex flex-col items-start gap-4 rounded-2xl border p-6 text-left shadow-sm transition-all"
        >
          <div className="bg-primary/10 text-primary flex h-12 w-12 items-center justify-center rounded-full">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            <h2 className="text-base font-semibold">Vaults I Help With</h2>
            <p className="text-muted-foreground text-sm">
              View the vaults where someone has chosen you as a trustee.
            </p>
          </div>
          <span className="text-primary mt-2 text-xs font-semibold">
            Continue as a trustee →
          </span>
        </button>
      </div>
    </div>
  );
}
